import WebApp from '@twa-dev/sdk';

import { copyToClipboard } from '@/shared/helpers/copy-to-clipboard';
import { parseJwt } from '@/shared/helpers/parse-JWT';
import { showPopup } from '@/shared/helpers/show-popup';

/* eslint-disable @typescript-eslint/no-misused-promises */
const isDev = import.meta.env.DEV;

// Fetch and return auth token from cookies
const getAuthToken = (): string | null => {
  const tokenRow = document.cookie.split('; ').find((row) => row.startsWith('token='));
  return tokenRow ? tokenRow.split('=')[1] : null;
};

// Sends error logs to the server
const logErrorInfo = async (body: Record<string, any>) => {
  const token = getAuthToken();

  if (!token) {
    console.warn('Token not found in cookies.');
    return;
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5-second timeout

    const response = await fetch(
      'https://api.playdeck.io/api/v1/sentry/log?source=MARKET',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
        signal: controller.signal, // Enables request timeout
      }
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`Failed to send error log: ${response.statusText}`);
    }

    const parsedToken = parseJwt(token);

    // Only show alert in production
    if (parsedToken?.authorities?.includes('ROLE_KASSIR')) {
      const message: string = body.message || 'Unknown error';

      if (WebApp) {
        showPopup(
          {
            title: 'Critical error occurred',
            message,
            buttons: [
              { type: 'close', id: 'close' },
              { type: 'default', id: 'copy', text: 'Copy' },
            ],
          },
          (buttonId: string | undefined) => {
            if (buttonId === 'copy') {
              copyToClipboard(JSON.stringify(body, null, '  ')).catch((err) => {
                showPopup({ message: err.message });
                console.error(err);
              });
            }
          }
        );
      } else {
        alert('Critical error occurred: ' + message);
      }
    }
  } catch (error) {
    console.error('[Error Sending Log]:', error);
  }
};

// Global error handler
window.addEventListener('error', async (event) => {
  // XXX Ignore errors that will be processed by componentDidCatch.
  // SEE: https://github.com/facebook/react/issues/10474
  if (event.error?.stack && event.error.stack.indexOf('invokeGuardedCallbackDev') >= 0) {
    return true;
  }

  try {
    const errorMessage = event.error?.message || event.message || 'Unknown error';

    const errorInfo = {
      type: 'ERROR',
      message: errorMessage,
      stacktrace: event.error?.stack || '',
      tags: {
        tg_id:
          WebApp?.initDataUnsafe?.user?.id !== undefined
            ? String(WebApp?.initDataUnsafe?.user?.id)
            : 'undefined',
      },
      extra: {
        place: 'early_error_handler',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        type: event.type,
        time: new Date().toISOString(),
      },
    };

    if (isDev) {
      console.error('[Error Caught]:', errorInfo);
    } else {
      await logErrorInfo(errorInfo);
    }
  } catch (e) {
    console.error('[Error Handling Error]:', e);
  }
});

// Global unhandled promise rejection handler
window.addEventListener('unhandledrejection', async (event) => {
  // XXX Ignore errors that will be processed by componentDidCatch.
  // SEE: https://github.com/facebook/react/issues/10474
  if (
    event.reason?.stack &&
    event.reason.stack.indexOf('invokeGuardedCallbackDev') >= 0
  ) {
    return true;
  }

  try {
    const errorMessage =
      event.reason?.message ||
      String(event.reason) ||
      'Unknown unhandled promise rejection';

    const errorInfo = {
      type: 'ERROR',
      message: errorMessage,
      stacktrace: event.reason?.stack || '',
      tags: {
        tg_id:
          WebApp?.initDataUnsafe?.user?.id !== undefined
            ? String(WebApp?.initDataUnsafe?.user?.id)
            : 'undefined',
      },
      extra: {
        place: 'early_rejection_handler',
        type: event.type,
        time: new Date().toISOString(),
      },
    };

    if (isDev) {
      console.error('[Unhandled Promise Rejection]:', event.reason);
    } else {
      await logErrorInfo(errorInfo);
    }
  } catch (error) {
    console.error('[Error Handling Rejection]:', error);
  }
});
